import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 border border-background-200/70 text-foreground-400 text-2xl">
              <i className="ri-article-line"></i>
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章不存在</h2>
            <p className="text-foreground-600 text-sm mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.imageUrlLarge,
      datePublished: article.publishedAt,
      author: {
        '@type': 'Organization',
        name: '51暗网',
      },
      publisher: {
        '@type': 'Organization',
        name: '51暗网',
      },
      url: `http://chigualiao.cn/article/${article.slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '51暗网',
        url: 'http://chigualiao.cn/',
      },
    },
  ];

  return (
    <>
      <PageMeta
        title={`${article.title} - 51暗网`}
        description={article.excerpt.replace(/<[^>]*>/g, '').slice(0, 150)}
        keywords={article.tags.join(',') + ',51暗网,吃瓜黑料'}
        canonicalUrl={`http://chigualiao.cn/article/${article.slug}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 bg-background-100 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
              { label: article.title },
            ]}
          />
        </div>

        <article className="py-12 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-accent-50 text-accent-700 border border-accent-200 hover:bg-accent-100 transition-colors mb-4 whitespace-nowrap"
                  >
                    <i className={category.icon}></i>
                    {category.name}
                  </Link>
                )}
                <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                  </span>
                  <span className="text-foreground-400">来源：51暗网编辑部</span>
                </div>
              </div>

              <div className="relative w-full mb-10 rounded-lg overflow-hidden" style={{ aspectRatio: '2/1' }}>
                <img
                  src={article.imageUrlLarge}
                  alt={article.title}
                  title={article.title}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="article-content prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-foreground-500 font-medium">标签：</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${article.category}`}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-50 text-secondary-700 border border-secondary-200 hover:bg-secondary-100 transition-colors whitespace-nowrap"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-8 flex items-center justify-between bg-background-100 rounded-lg border border-background-200/70 p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-foreground-500">分享到：</span>
                    {[
                      { icon: 'ri-weibo-line', label: '微博' },
                      { icon: 'ri-wechat-line', label: '微信' },
                      { icon: 'ri-twitter-x-line', label: 'Twitter' },
                    ].map((share) => (
                      <a
                        key={share.label}
                        href="#"
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-600 hover:text-primary-600 hover:border-primary-300 transition-colors cursor-pointer"
                        aria-label={`分享到${share.label}`}
                        rel="nofollow"
                      >
                        <i className={`${share.icon} text-sm`}></i>
                      </a>
                    ))}
                  </div>
                  <span className="text-[11px] text-foreground-400">
                    声明：本文由51暗网编辑部整理发布，仅供参考
                  </span>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                  相关爆料
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedArticles.map((rel) => {
                    const relCat = categories.find((c) => c.slug === rel.category);
                    return (
                      <ArticleCard
                        key={rel.id}
                        slug={rel.slug}
                        title={rel.title}
                        excerpt={rel.excerpt}
                        categoryName={relCat?.name || rel.category}
                        imageUrl={rel.imageUrl}
                        imageUrlCompact={rel.imageUrlCompact}
                        publishedAt={rel.publishedAt}
                        views={rel.views}
                        tags={rel.tags}
                        variant="compact"
                      />
                    );
                  })}
                </div>
                {category && (
                  <div className="text-center mt-8">
                    <Link
                      to={`/category/${category.slug}`}
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-50 transition-colors whitespace-nowrap"
                    >
                      查看更多{category.name}
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}